import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { siteConfig } from '../../data/siteConfig';
import { imageConfig } from '../../data/imageConfig';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close menu on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = (path) => {
    setIsMenuOpen(false);
    
    // Handle anchor links
    if (path.startsWith('#')) {
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const isActivePath = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
      <div className="container">
        <div className="header__content">
          {/* Logo & Brand */}
          <Link to="/" className="header__brand" onClick={() => handleLinkClick('/')}>
            <div className="brand-logo">
              <img 
                src={imageConfig.brand.logo} 
                alt={imageConfig.brand.logoAlt}
                className="brand-logo__image"
              />
            </div>
            <div className="brand-text">
              <h1 className="brand-name">{siteConfig.brand.name}</h1>
              <p className="brand-tagline">{siteConfig.brand.tagline}</p>
            </div>
          </Link>

          {/* Opening Soon Badge */}
          {siteConfig.hours.status === 'opening_soon' && (
            <div className="opening-soon-badge">
              {siteConfig.hours.message}
            </div>
          )}

          {/* Desktop Navigation */}
          <nav className="header__nav header__nav--desktop">
            <ul className="nav-list">
              {siteConfig.navigation.map((item) => (
                <li key={item.path} className="nav-item">
                  {item.path.startsWith('#') ? (
                    <button 
                      className={`nav-link ${isActivePath(item.path) ? 'nav-link--active' : ''}`}
                      onClick={() => handleLinkClick(item.path)}
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link 
                      to={item.path}
                      className={`nav-link ${isActivePath(item.path) ? 'nav-link--active' : ''}`}
                      onClick={() => handleLinkClick(item.path)}
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className={`menu-toggle ${isMenuOpen ? 'menu-toggle--active' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span className="menu-toggle__line"></span>
            <span className="menu-toggle__line"></span>
            <span className="menu-toggle__line"></span>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`header__nav--mobile ${isMenuOpen ? 'header__nav--mobile-open' : ''}`}>
        <nav className="mobile-nav">
          <ul className="mobile-nav__list">
            {siteConfig.navigation.map((item) => (
              <li key={item.path} className="mobile-nav__item">
                {item.path.startsWith('#') ? (
                  <button 
                    className={`mobile-nav__link ${isActivePath(item.path) ? 'mobile-nav__link--active' : ''}`}
                    onClick={() => handleLinkClick(item.path)}
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link 
                    to={item.path}
                    className={`mobile-nav__link ${isActivePath(item.path) ? 'mobile-nav__link--active' : ''}`}
                    onClick={() => handleLinkClick(item.path)}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="mobile-nav__overlay"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
};

export default Header;